/**
 * AuthZEN Subject Validation
 * 
 * Ensures subjects are always valid per OpenID AuthZEN 1.0 Draft 4 Section 5.1
 * 
 * Spec Requirements:
 * - Subject MUST have 'type' field (string)
 * - Subject MUST have 'id' field (string)
 * - Subject MAY have 'properties' field (object)
 */

import { getAuthzenSubject } from './subject';

export interface AuthZENSubject {
	type: string;
	id: string;
	properties?: Record<string, any>;
}

export class SubjectValidationError extends Error {
	constructor(message: string, public readonly field?: string) {
		super(message);
		this.name = 'SubjectValidationError';
	}
}

/**
 * Get and validate subject for authorization requests
 * 
 * Throws SubjectValidationError if subject is invalid.
 * This ensures we never send requests without proper subject.
 * 
 * @throws {SubjectValidationError} If subject is invalid or missing
 * @returns {AuthZENSubject} Validated subject
 * 
 * @example
 * ```typescript
 * try {
 *   const subject = await getValidatedSubject();
 *   // subject is guaranteed to have type and id
 * } catch (error) {
 *   if (error instanceof SubjectValidationError) {
 *     console.error('Subject validation failed:', error.message);
 *   }
 * }
 * ```
 */
export async function getValidatedSubject(): Promise<AuthZENSubject> {
	try {
		const subject = await getAuthzenSubject();
		
		// Validate subject exists
		if (!subject) {
			throw new SubjectValidationError(
				'Subject is required for authorization checks. User may not be authenticated.',
				'subject'
			);
		}
		
		// Validate required 'type' field (OpenID AuthZEN Section 5.1)
		if (!subject.type) {
			throw new SubjectValidationError(
				'Subject.type is required (OpenID AuthZEN Section 5.1)',
				'type'
			);
		}
		
		if (typeof subject.type !== 'string') {
			throw new SubjectValidationError(
				`Subject.type must be string, got ${typeof subject.type}`,
				'type'
			);
		}
		
		// Validate required 'id' field (OpenID AuthZEN Section 5.1)
		if (!subject.id) {
			throw new SubjectValidationError(
				'Subject.id is required (OpenID AuthZEN Section 5.1)',
				'id'
			);
		}
		
		if (typeof subject.id !== 'string') {
			throw new SubjectValidationError(
				`Subject.id must be string, got ${typeof subject.id}`,
				'id'
			);
		}
		
		// Validate optional 'properties' field if present
		if ('properties' in subject && subject.properties !== null && subject.properties !== undefined) {
			if (typeof subject.properties !== 'object') {
				throw new SubjectValidationError(
					`Subject.properties must be object if present, got ${typeof subject.properties}`,
					'properties'
				);
			}
		}
		
		return subject as AuthZENSubject;
		
	} catch (error) {
		// Re-throw SubjectValidationError as-is
		if (error instanceof SubjectValidationError) {
			throw error;
		}
		
		// Wrap unexpected errors
		throw new SubjectValidationError(
			`Failed to get subject: ${error instanceof Error ? error.message : String(error)}`
		);
	}
}

/**
 * Check if current user has a valid subject without throwing
 * 
 * Useful for conditional rendering where you want to check
 * if user is authenticated before attempting authorization.
 * 
 * @returns {boolean} True if subject is valid, false otherwise
 * 
 * @example
 * ```typescript
 * if (await hasValidSubject()) {
 *   // Show authenticated UI
 * } else {
 *   // Show login prompt
 * }
 * ```
 */
export async function hasValidSubject(): Promise<boolean> {
	try {
		await getValidatedSubject();
		return true;
	} catch {
		return false;
	}
}

/**
 * Get subject or return anonymous placeholder
 * 
 * For cases where you want to degrade gracefully to anonymous access
 * instead of throwing an error.
 * 
 * @returns {AuthZENSubject} Validated subject or anonymous placeholder
 * 
 * @example
 * ```typescript
 * const subject = await getSubjectOrAnonymous();
 * // Always returns valid subject (authenticated or anonymous)
 * ```
 */
export async function getSubjectOrAnonymous(): Promise<AuthZENSubject> {
	try {
		return await getValidatedSubject();
	} catch {
		// Return anonymous subject for degraded access
		return {
			type: 'anonymous',
			id: 'anonymous',
			properties: {
				authenticated: false
			}
		};
	}
}

